import { negotiateAPI } from "..";

export type HallSettlementMode = "prepaid" | "legacy";
export type HallReadinessPolicy = {
  hallSettlementMode?: HallSettlementMode;
  negotiationBalanceReady?: boolean;
};

export class HallModeUnavailableError extends Error {
  readonly status = 503;
  readonly code = "payment_readiness_unavailable";
  constructor() {
    super("Hall settlement mode unavailable");
    this.name = "HallModeUnavailableError";
  }
}

const inFlight = new Map<string, Promise<HallSettlementMode>>();

/** An active cycle owns its rail; the configured policy describes future cycles only. */
export function readHallSettlementMode(
  jwt: string,
  policy: HallReadinessPolicy,
): Promise<HallSettlementMode> {
  // This is the readiness contract of the pre-cutover release. Activation
  // requires the newer readiness producer before prepaid cycles may open.
  if (policy.hallSettlementMode === undefined && policy.negotiationBalanceReady === undefined)
    return Promise.resolve("legacy");
  if (policy.hallSettlementMode !== "prepaid" && policy.hallSettlementMode !== "legacy")
    return Promise.reject(new HallModeUnavailableError());
  const configured = policy.hallSettlementMode;
  const key = `${jwt}:${configured}`;
  const current = inFlight.get(key);
  if (current) return current;
  const read = (async (): Promise<HallSettlementMode> => {
    try {
      const response = await negotiateAPI.get("/cycle-mode", {
        headers: { "X-JWT": jwt },
        timeout: 10_000,
      });
      const body = response.data;
      if (
        !body ||
        typeof body.cycleId !== "string" ||
        !body.cycleId ||
        (body.settlementMode !== "prepaid" && body.settlementMode !== "legacy")
      )
        throw new HallModeUnavailableError();
      return body.settlementMode;
    } catch (error) {
      const response = (error as { response?: { status?: number; data?: { reason?: string } } })
        ?.response;
      // An explicit absence lets creative managers prepare for the next
      // cycle. A missing route or an unreadable active cycle proves nothing.
      if (response?.status === 404 && response.data?.reason === "no_active_cycle")
        return configured;
      throw new HallModeUnavailableError();
    }
  })().finally(() => inFlight.delete(key));
  inFlight.set(key, read);
  return read;
}
